import React, {useMemo, useState} from "react";
import {useQuery} from "@apollo/client";
import NavBar from "../../components/common/NavBar";
import PaginatedCardList from "../../components/pagination/PaginatedCardList";
import useTranslation from "../../i18n/useTranslation";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import {GET_MANAGERS} from "../../components/staff/queries/GetManagers";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import InitialsAvatar from "../../util/name_initial";
import {alphaFromHex} from "../../util/colors";
import AddStaffMemberModal from "../../components/staff/AddStaffMemberModal";
import {GET_STAFF_ORGANIZATIONS} from "../../components/staff/queries/GetOrganizations";
import useActiveProfileOrgProOrElite from "../../components/common/hooks/useActiveProfileOrgProOrElite";
import UpgradeNotice, {UpgradeNoticeLink} from "../../components/common/UpgradeNotice";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import CreateOrganizationUserModal from "../../components/organization/CreateOrganizationUserModal";

const ROLE_MANAGER = 1;
const ROLE_COORDINATOR = 6;
const VISIBLE_ROLE_IDS = new Set([ROLE_MANAGER, ROLE_COORDINATOR]);

function resolveRoleLabel(groupId, t) {
    const normalizedId = Number(groupId);
    if (normalizedId === ROLE_MANAGER) return t("staffPage.roles.manager");
    if (normalizedId === ROLE_COORDINATOR) return t("staffPage.roles.coordinator");
    return null;
}

export default function StaffPage() {
    const {t} = useTranslation();
    const colors = useThemeColors();
    const {activeProfile} = useActiveProfile();

    const [page, setPage] = useState(1);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const pageSize = 12;
    const {hasAnyOrgEnterprise} = useActiveProfileOrgProOrElite();

    const {data, loading, error, refetch} = useQuery(GET_MANAGERS, {
        variables: {
            q: "",
            page,
            pageSize,
        },
        skip: !activeProfile?.id || !hasAnyOrgEnterprise,
        fetchPolicy: "cache-and-network",
    });

    const {data: organizationsData} = useQuery(GET_STAFF_ORGANIZATIONS, {
        variables: {
            profileId: activeProfile?.id ?? 0,
        },
        skip: !activeProfile?.id,
        fetchPolicy: "cache-and-network",
    });

    const managers = useMemo(() => {
        return Array.isArray(data?.managers?.objects) ? data.managers.objects : [];
    }, [data?.managers?.objects]);

    const totalPages = useMemo(() => {
        return Number(data?.managers?.pages) || 1;
    }, [data?.managers?.pages]);

    const organizationOptions = useMemo(() => {
        const organizations = Array.isArray(organizationsData?.organizations?.objects)
            ? organizationsData.organizations.objects
            : [];

        return organizations
            .filter((organization) => Number(organization?.id))
            .map((organization) => ({
                id: Number(organization.id),
                name: organization.name || "",
                slug: organization.slug || "",
            }));
    }, [organizationsData?.organizations?.objects]);

    const openAddModal = () => setShowAddModal(true);
    const closeAddModal = () => setShowAddModal(false);

    const handleInvited = async () => {
        await refetch({q: "", page, pageSize});
    };

    const navBarElem = useMemo(
        () => ({
            pageTitle: t("staffPage.pageTitle"),
            links: [
                {pageTitle: t("staffPage.settings"), navLink: null, active: false},
                {pageTitle: t("staffPage.pageTitle"), navLink: null, active: true},
            ],
        }),
        [t]
    );

    const cardUi = useMemo(
        () => ({
            card: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.09)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                minHeight: 360,
            },
            header: {
                background: `linear-gradient(
                135deg,
                ${alphaFromHex(colors.primary, 0.42)},
                ${alphaFromHex(colors.surface, 0.05)}
            )`,
                color: colors.textOnPrimary,
                paddingTop: 14,
                paddingBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 12,
                borderBottom: `1px solid ${alphaFromHex(colors.primary, 0.20)}`,
            },
            headerTextWrap: {
                minWidth: 0,
                flex: 1,
            },
            userName: {
                fontSize: 17,
                lineHeight: 1.2,
                fontWeight: 800,
                letterSpacing: "0.01em",
            },
            userRole: {
                fontSize: 13,
                lineHeight: 1.2,
                fontWeight: 700,
                opacity: 0.88,
            },
            listItem: {
                paddingTop: 10,
                paddingBottom: 10,
                fontWeight: 700,
                gap: 10,
                borderTop: "1px solid rgba(0,0,0,0.05)",
            },
            footer: {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                background: alphaFromHex(colors.surface, 0.12),
            },
            orgList: {
                flex: 1,
                maxHeight: 210,
                overflowY: "auto",
            },
            orgName: {
                color: "rgba(20,21,26,0.86)",
                fontSize: 13.5,
            },
            roleBadge: (groupId) => ({
                backgroundColor:
                    Number(groupId) === ROLE_COORDINATOR ? colors.secondary : colors.primary,
                color:
                    Number(groupId) === ROLE_COORDINATOR
                        ? colors.textOnSecondary
                        : colors.textOnPrimary,
                borderRadius: 999,
                fontWeight: 800,
                letterSpacing: "0.01em",
                padding: "5px 10px",
                fontSize: 11.5,
                border: "1px solid rgba(0,0,0,0.12)",
            }),
        }),
        [colors.primary, colors.secondary, colors.textOnPrimary, colors.textOnSecondary, colors.surface]
    );

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={navBarElem}/>

            <section className="content">
                <div className="container-fluid">
                    {error ? (
                        <div className="alert alert-danger col-6 mx-auto text-center">{getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}</div>
                    ) : null}

                    {!hasAnyOrgEnterprise ? (
                        <UpgradeNotice
                            planLabel={t("staffPage.upgradePlanLabel")}
                            title={t("staffPage.upgradeTitle")}
                            subtitle={t("staffPage.upgradeSubtitle")}
                            ctaLabel={t("staffPage.upgradeCta")}
                            to="/subscription/plans"
                        >
                            {t("staffPage.upgradeTextPrefix")}{" "}
                            <UpgradeNoticeLink to="/subscription/plans">
                                {t("staffPage.upgradeTextLink")}
                            </UpgradeNoticeLink>
                            .
                        </UpgradeNotice>
                    ) : (
                        <PaginatedCardList
                            title={t("staffPage.pageTitle")}
                            subtitle={t("staffPage.subtitle")}
                            onAdd={openAddModal}
                            addActions={[
                                {label: t("common.table.add"), iconClass: "fas fa-user-plus", onClick: openAddModal},
                                {label: t("organizations.createUserSubmit"), iconClass: "fas fa-user-plus", onClick: () => setShowCreateUser(true)},
                            ]}
                            page={page}
                            pages={totalPages}
                            onPageChange={setPage}
                            items={managers}
                            loading={loading}
                            renderItem={(manager) => {
                                const managerOrganizations = Array.isArray(manager?.profileOrganizations)
                                    ? manager.profileOrganizations
                                    : [];

                                const visibleOrganizations = managerOrganizations.filter((profileOrg) =>
                                    VISIBLE_ROLE_IDS.has(Number(profileOrg?.group?.id))
                                );

                                const roleLabels = [
                                    ...new Set(
                                        visibleOrganizations.map((profileOrg) =>
                                            resolveRoleLabel(profileOrg?.group?.id, t)
                                        ).filter(Boolean)
                                    ),
                                ];

                                const roleDescription = roleLabels.length
                                    ? roleLabels.join(" / ")
                                    : "-";

                                return (
                                    <div
                                        key={manager.id}
                                        className="col-12 col-sm-12 col-md-6 d-flex align-items-stretch flex-column"
                                    >
                                        <div
                                            className="card card-widget widget-user-2 staff-widget-card h-100 d-flex flex-column"
                                            style={cardUi.card}>
                                            <div
                                                className="widget-user-header"
                                                style={cardUi.header}
                                            >
                                                <div className="widget-user-image"
                                                     style={{marginRight: 0, position: "static"}}>
                                                    {manager?.logoUrl ? (
                                                        <img
                                                            className="img-circle elevation-2"
                                                            src={manager.logoUrl}
                                                            alt={manager.fullName || "Staff avatar"}
                                                            style={{width: 52, height: 52, objectFit: "cover"}}
                                                        />
                                                    ) : (
                                                        <InitialsAvatar
                                                            name={manager.fullName}
                                                            size={52}
                                                            backgroundColor={colors.primary}
                                                            textColor={colors.textOnPrimary}
                                                        />
                                                    )}
                                                </div>
                                                <div style={cardUi.headerTextWrap}>
                                                    <h3 className="widget-user-username text-truncate mb-1"
                                                        title={manager.fullName} style={cardUi.userName}>
                                                        {manager.fullName}
                                                    </h3>
                                                    <h5 className="widget-user-desc mb-0 text-truncate"
                                                        title={roleDescription} style={cardUi.userRole}>
                                                        {roleDescription}
                                                    </h5>
                                                </div>
                                            </div>

                                            <div className="card-footer p-0" style={cardUi.footer}>
                                                {!visibleOrganizations.length ? (
                                                    <ul className="nav flex-column" style={cardUi.orgList}>
                                                        <li className="nav-item">
                                                        <span className="nav-link text-muted">
                                                            {t("staffPage.noOrganizations")}
                                                        </span>
                                                        </li>
                                                    </ul>
                                                ) : (
                                                    <ul className="nav flex-column" style={cardUi.orgList}>
                                                        {visibleOrganizations.map((profileOrg, index) => {
                                                            const roleLabel = resolveRoleLabel(profileOrg?.group?.id, t);
                                                            return (
                                                                <li
                                                                    key={`${manager.id}-${profileOrg?.organization?.name || "org"}-${index}`}
                                                                    className="nav-item"
                                                                >
                                                            <span
                                                                className="nav-link d-flex align-items-center justify-content-between"
                                                                style={cardUi.listItem}>
                                                                <span className="text-truncate"
                                                                      title={profileOrg?.organization?.name || t("staffPage.noOrganizationName")}
                                                                      style={cardUi.orgName}>
                                                                    {profileOrg?.organization?.name || t("staffPage.noOrganizationName")}
                                                                </span>
                                                                {roleLabel ? (
                                                                    <span className="badge"
                                                                          style={cardUi.roleBadge(profileOrg?.group?.id)}>
                                                                        {roleLabel}
                                                                    </span>
                                                                ) : null}
                                                            </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    )}
                </div>
            </section>

            <AddStaffMemberModal
                show={showAddModal}
                onClose={closeAddModal}
                profileId={activeProfile?.id}
                organizations={organizationOptions}
                colors={colors}
                onInvited={handleInvited}
            />

            <CreateOrganizationUserModal
                show={showCreateUser}
                onClose={() => setShowCreateUser(false)}
                organizations={organizationOptions}
                role={ROLE_COORDINATOR}
                colors={colors}
                onCreated={() => refetch()}
            />

            <style>{`
                .staff-widget-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 18px 36px rgba(0,0,0,0.15);
                    border-color: rgba(0,0,0,0.14);
                }

                .staff-widget-card .nav-item:first-child .nav-link {
                    border-top: none;
                }

                .staff-widget-card .nav-link:hover {
                    background: rgba(0,0,0,0.02);
                }

                .staff-widget-card .nav.flex-column::-webkit-scrollbar {
                    width: 7px;
                }

                .staff-widget-card .nav.flex-column::-webkit-scrollbar-thumb {
                    background: rgba(0,0,0,0.22);
                    border-radius: 999px;
                }
            `}</style>
        </div>
    );
}





